import { useEffect, useMemo, useRef } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { CursorPage } from "@/api/types";

export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  query: UseInfiniteQueryResult<{ pages: CursorPage<T>[]; pageParams: unknown[] }, Error>;
  rowId: (row: T) => string;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (s: RowSelectionState) => void;
  rowHeight?: number;
  className?: string;
  emptyState?: React.ReactNode;
}

/**
 * Generic virtualized infinite-scroll table. Pulls pages from a TanStack
 * `useInfiniteQuery` returning `CursorPage<T>` and pre-fetches the next page
 * when the viewport gets within 5 rows of the bottom.
 */
export function DataTable<T>({
  columns,
  query,
  rowId,
  rowSelection,
  onRowSelectionChange,
  rowHeight = 36,
  className,
  emptyState,
}: DataTableProps<T>) {
  const flatRows = useMemo<T[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  const table = useReactTable<T>({
    data: flatRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: rowId,
    state: { rowSelection: rowSelection ?? {} },
    onRowSelectionChange: (updater) => {
      if (!onRowSelectionChange) return;
      const next =
        typeof updater === "function"
          ? updater(rowSelection ?? {})
          : updater;
      onRowSelectionChange(next);
    },
    enableRowSelection: !!onRowSelectionChange,
  });

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const rows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  // Infinite-scroll trigger.
  useEffect(() => {
    const items = virtualizer.getVirtualItems();
    const last = items.at(-1);
    if (!last) return;
    if (
      last.index >= rows.length - 5 &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      query.fetchNextPage();
    }
  }, [
    virtualizer.getVirtualItems(),
    rows.length,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
  ]);

  const totalSize = virtualizer.getTotalSize();
  const items = virtualizer.getVirtualItems();
  const paddingTop = items[0]?.start ?? 0;
  const paddingBottom = totalSize - (items.at(-1)?.end ?? 0);

  if (rows.length === 0 && !query.isLoading) {
    return <div>{emptyState ?? null}</div>;
  }

  return (
    <div
      ref={scrollerRef}
      className={`overflow-auto ${className ?? "h-[70vh]"}`}
    >
      <table className="w-full min-w-[44rem] text-sm">
        <thead className="sticky top-0 z-10 bg-card">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="text-xs text-muted">
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  className="px-3 py-2 text-left font-medium border-b border-border"
                  style={{ width: h.getSize() === 150 ? undefined : h.getSize() }}
                >
                  {h.isPlaceholder
                    ? null
                    : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 ? (
            <tr><td style={{ height: paddingTop }} colSpan={columns.length} /></tr>
          ) : null}
          {items.map((vrow) => {
            const row = rows[vrow.index];
            return (
              <tr
                key={row.id}
                data-index={vrow.index}
                className="hover:bg-subtle/40 border-b border-border"
                style={{ height: rowHeight }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-1.5 align-middle">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {paddingBottom > 0 ? (
            <tr><td style={{ height: paddingBottom }} colSpan={columns.length} /></tr>
          ) : null}
        </tbody>
      </table>
      {query.isFetchingNextPage ? (
        <div className="px-4 py-2 text-xs text-muted text-center">Loading more…</div>
      ) : null}
    </div>
  );
}
